import React, { useState, useRef } from 'react';
import { uploadImage, getImagePreview, validateImageFile } from '../services/imageUploadService';
import { icons } from './ui/Icons';
import Button from './ui/Button';
import toast from 'react-hot-toast';

/**
 * Componente: ImageUpload
 * Upload de imagens com preview e drag & drop
 */
const ImageUpload = ({ 
  currentImageUrl = '', 
  onImageUploaded, 
  userId = 'temp', // ID do usuário para organizar no Storage
  folder = 'profiles', // Pasta no Storage
  label = 'Upload de Imagem',
  aspectRatio = 'aspect-video', // 'aspect-video' (16:9) | 'aspect-square' (1:1) | 'aspect-[3/4]'
  maxSizeMB = 5,
  recommendedSize = '1200x400px'
}) => {
  const [preview, setPreview] = useState(currentImageUrl);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validar arquivo
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      // Mostrar preview
      const previewUrl = await getImagePreview(file);
      setPreview(previewUrl);

      // Fazer upload com userId e folder
      setUploading(true);
      const result = await uploadImage(file, userId, folder);

      if (result.success) {
        toast.success('Imagem enviada com sucesso!');
        onImageUploaded(result.data.url, result.data.path);
        setPreview(result.data.url);
      } else {
        toast.error(result.error || 'Erro ao fazer upload');
        setPreview(currentImageUrl); // Reverter para imagem anterior
      }
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      toast.error('Erro ao processar imagem');
      setPreview(currentImageUrl);
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreview('');
    onImageUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      {/* Área de Upload */}
      <div
        className={`relative ${aspectRatio} w-full border-2 border-dashed rounded-lg overflow-hidden transition-all ${
          dragActive
            ? 'border-indigo-500 bg-indigo-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {preview ? (
          // Preview da Imagem
          <div className="relative w-full h-full group">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
            
            {/* Overlay com botões */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClick}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {icons.pencil({ className: 'w-4 h-4 mr-2' })}
                Alterar
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleRemove}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {icons.trash({ className: 'w-4 h-4 mr-2' })}
                Remover
              </Button>
            </div>
          </div>
        ) : (
          // Área de Upload Vazia
          <div
            className="w-full h-full flex flex-col items-center justify-center p-6 cursor-pointer text-gray-500 hover:text-gray-700 transition-colors"
            onClick={handleClick}
          >
            <svg
              className="w-12 h-12 mb-3"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-sm font-medium mb-1">
              Clique ou arraste uma imagem
            </p>
            <p className="text-xs text-gray-400">
              JPG, PNG, GIF ou WebP (max {maxSizeMB}MB)
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Tamanho recomendado: {recommendedSize}
            </p>
          </div>
        )}

        {/* Loading Overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Enviando...</p>
            </div>
          </div>
        )}
      </div>

      {/* Input Oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleChange}
        className="hidden"
      />

      {/* Dica */}
      <p className="text-xs text-gray-500">
        💡 Dica: Imagens otimizadas carregam mais rápido e melhoram a experiência do usuário
      </p>
    </div>
  );
};

export default ImageUpload;
